/**
 * ⭐⭐ THE CITY INDEX — search any city, and name the one nearest a point.
 *
 * Settings → Location used to offer 18 hand-picked cities, so a tester in Dammam
 * and the owner in Barcelona both had to look up coordinates by hand, and
 * "Use my location" never said where it thought you were (owner, 2026-09-17).
 * The index is GeoNames' 34k cities over 15,000 people, parsed from one TSV
 * string (`data/worldCities.ts` loads it as its own lazy chunk).
 *
 * ⚠️⚠️ EVERY CITY CARRIES ITS OWN IANA TIME ZONE, and that is the point: the day
 * clock and the prayer engine need the zone of the PLACE. Before this, GPS and
 * typed coordinates took the DEVICE's zone — a phone still set to Riyadh with
 * Barcelona coordinates produced Barcelona prayer times on Riyadh's clock.
 */

export interface City {
  /** Display name, diacritics intact ("Köln", "São Paulo"). */
  name: string;
  /** Region code where it is a letter code (US/CA states) — '' otherwise. */
  region: string;
  /** ISO country code ("SA", "ES"). */
  country: string;
  lat: number;
  lng: number;
  /** Thousands of people — the search's tie-breaker. */
  popK: number;
  timeZone: string;
  /** Lower-cased, diacritic-free name + alternative spellings, for matching. */
  search: string;
}

export interface CityIndex {
  cities: City[];
}

/** Lower-case and strip diacritics, so "Köln" matches "koln" and "Málaga" "malaga". */
export function foldName(s: string): string {
  return s.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase().trim();
}

/**
 * Parse the generated TSV. First line is the time-zone table; every later line is
 * one city. ⚠️ Kept O(n) and allocation-light: this runs once over 34k rows.
 */
export function parseCityData(text: string): CityIndex {
  const lines = text.split('\n');
  const zones = (lines[0] ?? '').split('\t');
  const cities: City[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const [name, aliases, country, region, lat, lng, popK, tz] = line.split('\t');
    const timeZone = zones[Number(tz)];
    if (!name || !country || !timeZone) continue;
    cities.push({
      name,
      region: region || '',
      country,
      lat: Number(lat) / 1e3,
      lng: Number(lng) / 1e3,
      popK: Number(popK) || 0,
      timeZone,
      search: foldName(aliases ? `${name} ${aliases.split('|').join(' ')}` : name),
    });
  }
  return { cities };
}

/** "Dammam, SA" — with the region where there is one ("Florence, AL, US"). */
export function cityLabel(c: City): string {
  return c.region ? `${c.name}, ${c.region}, ${c.country}` : `${c.name}, ${c.country}`;
}

/**
 * Cities matching a typed query, best first: a name that STARTS with the query
 * beats one that merely contains it, and within each the bigger city wins — so
 * "bar" offers Barcelona before Bardhaman.
 * ⚠️ The dataset is population-sorted, so ties keep that order.
 */
export function searchCities(index: CityIndex, query: string, limit = 20): City[] {
  const q = foldName(query);
  if (q.length < 2) return [];
  const starts: City[] = [];
  const contains: City[] = [];
  for (const c of index.cities) {
    const at = c.search.indexOf(q);
    if (at === -1) continue;
    // A match at 0, or at the start of any alternative spelling, reads as "starts with".
    if (at === 0 || c.search[at - 1] === ' ') {
      if (starts.length < limit) starts.push(c);
    } else if (contains.length < limit) {
      contains.push(c);
    }
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

const R_EARTH_KM = 6371;
const toRad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in km. */
export function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH_KM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * The nearest city to a point, with its distance — what turns a GPS fix into
 * "Near Dammam, SA" and gives typed coordinates a real time zone.
 * ⚠️ A plain scan of 34k rows (~1 ms). A cheap latitude pre-filter would break
 * near the poles and the ±180° meridian for no gain a reader would notice.
 */
export function nearestCity(index: CityIndex, lat: number, lng: number): { city: City; km: number } | null {
  let best: City | null = null;
  let bestKm = Infinity;
  for (const c of index.cities) {
    const km = distanceKm(lat, lng, c.lat, c.lng);
    if (km < bestKm) { bestKm = km; best = c; }
  }
  return best ? { city: best, km: bestKm } : null;
}
